import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="chat-document-preview"
//
// Side-by-side markdown preview for the chat-documents admin. Debounced POST
// to a server-rendered (redcarpet) endpoint — keeps the implementation tiny
// and avoids adding a client-side markdown library.
//
// Targets:
//   - source: <textarea> with markdown content
//   - preview: container that receives rendered HTML
//   - status: optional small text indicator
//
// Values:
//   - url: preview endpoint (POST, returns { html })
//   - debounce: ms to wait after last keystroke (default 350)
export default class extends Controller {
  static targets = ['source', 'preview', 'status'];
  static values = { url: String, debounce: { type: Number, default: 350 } };

  connect() {
    this.timer = null;
    if (this.hasSourceTarget) this.render();
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer);
  }

  scheduleRender() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => this.render(), this.debounceValue);
  }

  async render() {
    if (!this.hasSourceTarget || !this.hasPreviewTarget) return;
    const source = this.sourceTarget.value;

    if (!source.trim()) {
      this.previewTarget.innerHTML = '<em class="text-muted">— leer —</em>';
      this.setStatus('');
      return;
    }

    this.setStatus('rendert…');
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrf,
        },
        body: JSON.stringify({ content: source }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      this.previewTarget.innerHTML = data.html || '';
      this.setStatus(`gerendert (${source.length} Zeichen)`);
    } catch (err) {
      this.setStatus(`Fehler: ${err.message}`);
    }
  }

  setStatus(text) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = text;
  }
}
